<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Purchase',
  },
})

const route = useRoute()
const { can } = useAbility()
const { loading: confirmLoading, request } = useApiRequest()

const receiving    = ref(null)
const fetchLoading = ref(true)
const fetchError   = ref('')
const confirmDialog = ref(false)

const formatRupiah = val =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val ?? 0)

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'

const grandTotal = computed(() =>
  (receiving.value?.items ?? []).reduce((s, i) => s + (i.subtotal ?? 0), 0)
)

// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchReceiving = async () => {
  fetchLoading.value = true
  fetchError.value   = ''
  try {
    const response = await useApi(`/api/v1/purchase-receivings/${route.params.id}`).get()
    const body = response?.data ?? response
    receiving.value = body?.data ?? body
  } catch (err) {
    fetchError.value = 'Gagal memuat data receiving.'
    console.error('[ReceivingDetail] fetch error', err)
  } finally {
    fetchLoading.value = false
  }
}

onMounted(fetchReceiving)

// ─── Confirm → stok masuk ────────────────────────────────────────────────────
const doConfirm = async () => {
  const result = await request(() =>
    useApi(`/api/v1/purchase-receivings/${route.params.id}/confirm`).post({})
  )

  if (result !== null) {
    confirmDialog.value = false
    await fetchReceiving()
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <!-- Loading -->
      <VProgressLinear v-if="fetchLoading" indeterminate class="mb-4" />

      <!-- Error -->
      <VAlert v-else-if="fetchError" type="error" class="mb-4">
        {{ fetchError }}
      </VAlert>

      <template v-else-if="receiving">
        <!-- ─── Header Card ──────────────────────────────────────────────── -->
        <VCard class="mb-4">
          <VCardText class="d-flex flex-wrap gap-4 align-start justify-space-between pa-5">
            <!-- Info -->
            <div>
              <div class="text-h6 font-weight-bold">{{ receiving.receiving_number }}</div>
              <div class="text-body-2 text-medium-emphasis mt-1">
                <template v-if="receiving.purchase_order">
                  PO: <strong>{{ receiving.purchase_order.po_number }}</strong>
                  — {{ receiving.purchase_order.supplier ?? '-' }}
                </template>
                <template v-else>
                  Direct Receiving (tanpa PO)
                </template>
              </div>
            </div>

            <!-- Actions -->
            <div class="d-flex flex-wrap gap-2 align-center">
              <VChip
                :color="receiving.status === 'confirmed' ? 'success' : 'default'"
                label
              >
                {{ receiving.status === 'confirmed' ? 'Dikonfirmasi' : 'Draft' }}
              </VChip>

              <VBtn
                v-if="can('update', 'Purchase') && receiving.is_draft"
                color="success"
                prepend-icon="ri-check-double-line"
                @click="confirmDialog = true"
              >
                Konfirmasi & Masukkan Stok
              </VBtn>
            </div>
          </VCardText>

          <VDivider />

          <!-- Meta info -->
          <VCardText class="pa-5">
            <VRow>
              <VCol cols="6" md="3">
                <p class="text-caption text-medium-emphasis mb-1">Tgl. Terima</p>
                <p class="text-body-2">{{ formatDate(receiving.received_at) }}</p>
              </VCol>
              <VCol cols="6" md="3">
                <p class="text-caption text-medium-emphasis mb-1">Dibuat Oleh</p>
                <p class="text-body-2">{{ receiving.creator?.name ?? '-' }}</p>
              </VCol>
              <VCol v-if="receiving.notes" cols="12">
                <p class="text-caption text-medium-emphasis mb-1">Catatan</p>
                <p class="text-body-2">{{ receiving.notes }}</p>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- ─── Items Card ───────────────────────────────────────────────── -->
        <VCard class="mb-4">
          <VCardTitle class="pa-5 pb-3">Item yang Diterima</VCardTitle>
          <VDivider />

          <VTable>
            <thead>
              <tr>
                <th>Bahan Baku</th>
                <th>Satuan</th>
                <th class="text-end">Qty Diterima</th>
                <th class="text-end">Harga Satuan</th>
                <th class="text-end">Subtotal</th>
                <th>Kedaluwarsa</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in receiving.items" :key="item.id">
                <td>{{ item.ingredient_name }}</td>
                <td>{{ item.unit_symbol }}</td>
                <td class="text-end">{{ item.quantity_received }}</td>
                <td class="text-end">{{ formatRupiah(item.unit_cost) }}</td>
                <td class="text-end">{{ formatRupiah(item.subtotal) }}</td>
                <td>{{ item.expiry_date ?? '—' }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4" class="text-end font-weight-semibold py-3 pe-3">Total:</td>
                <td class="text-end font-weight-bold text-primary text-body-1">
                  {{ formatRupiah(grandTotal) }}
                </td>
                <td />
              </tr>
            </tfoot>
          </VTable>
        </VCard>

        <!-- ─── Confirmed info banner ────────────────────────────────────── -->
        <VAlert
          v-if="receiving.status === 'confirmed'"
          type="success"
          variant="tonal"
        >
          Receiving ini sudah dikonfirmasi. Stok bahan baku telah diperbarui dan
          <em>average cost</em> telah dikalkulasi ulang.
        </VAlert>
      </template>
    </VCol>
  </VRow>

  <!-- ─── Confirm Dialog ───────────────────────────────────────────────────── -->
  <VDialog v-model="confirmDialog" max-width="460" persistent>
    <VCard>
      <VCardTitle class="pt-5 px-5">Konfirmasi Receiving?</VCardTitle>
      <VCardText class="px-5">
        <p class="mb-2">
          Setelah dikonfirmasi, stok bahan baku akan <strong>langsung diperbarui</strong>
          dan tindakan ini <strong>tidak dapat dibatalkan</strong>.
        </p>
        <p class="text-caption text-medium-emphasis">
          Pastikan semua item, jumlah, dan harga sudah benar sebelum melanjutkan.
        </p>
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn variant="tonal" :disabled="confirmLoading" @click="confirmDialog = false">
          Batal
        </VBtn>
        <VBtn color="success" :loading="confirmLoading" @click="doConfirm">
          Ya, Konfirmasi & Masukkan Stok
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
